import React from 'react'
import Link from 'next/link'
import { MdChevronRight } from 'react-icons/md'
import { countryList } from 'utils/constants'

// Internal-link grid that connects a hub to its price pages, job pages,
// sub-services and guides. Each item is { href, label, desc? }.
export const RelatedLinks = ({ title = 'Related Services', links }) => {
    if (!links?.length) return null
    return (
        <section className="max-w-5xl mx-auto px-4 py-10">
            <h2 className="text-xl md:text-2xl font-bold text-center mb-5">{title}</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {links.map(({ href, label, desc }) => (
                    <li key={href}>
                        <Link
                            href={href}
                            prefetch={false}
                            className="flex items-center justify-between gap-3 min-h-14 h-full bg-white border border-gray-200 rounded-2xl px-4 py-3 hover:border-orange-300 hover:shadow-md transition"
                        >
                            <span className="min-w-0">
                                <span className="block text-sm font-bold text-gray-900">{label}</span>
                                {desc && <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>}
                            </span>
                            <MdChevronRight aria-hidden="true" className="shrink-0 text-xl text-orange-500" />
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    )
}

// The towns come from the same locality table that drives the aircon locality
// pages. They are plain text, not links: only aircon has locality pages, and
// linking there from another service would send "electrician near me" or
// "part time cleaner near me" visitors to the wrong service.
const sgLocalities = (countryList.find((c) => c.country_cd === 'sg')?.localityList || [])
    .filter((name) => name !== 'Singapore')

// `intro` is required copy for each service, because the premises and hours
// differ: technicians attend 8am-10pm daily, while helpers work Monday to Saturday.
export const AreasServed = ({ title, intro }) => (
    <section className="bg-gray-50 py-10 px-4">
        <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-bold mb-3">{title}</h2>
            <p className="text-sm text-gray-600 max-w-2xl mx-auto mb-5 leading-relaxed">{intro}</p>
            <ul className="flex flex-wrap justify-center gap-2" aria-label="Areas served">
                {sgLocalities.map((name) => (
                    <li key={name} className="text-xs text-gray-700 bg-white border border-gray-200 px-3 py-1.5 rounded-full">
                        {name}
                    </li>
                ))}
            </ul>
        </div>
    </section>
)

// Intro copy for the handyman, electrician and plumber pages. The wording is
// unchanged from the old HandymanAreasServed.
export const technicianAreasIntro = (serviceLabel) =>
    `We send a ${serviceLabel} to HDB flats, BTO and resale units, condominiums, landed homes and offices in every ` +
    `part of Singapore. Technicians attend from 8am to 10pm daily, and same-day slots are available subject to ` +
    `availability in your area.`
